import Phaser from "phaser";

import { emitFollowNudge } from "../game/player-follow.js";
import type { DialogShellMetrics } from "../game/dialog-layout.js";
import { UI_FONT } from "../game/ui-theme.js";

export const FOLLOW_NUDGE_GREEN = "#4ade80";
export const FOLLOW_NUDGED_ACK = "✓ Nudged!";
export const FOLLOW_NUDGE_FADE_MS = 1500;
const NUDGE_DEBOUNCE_MS = 300;

let lastNudgeEmitAt = 0;

export function emitFollowNudgeDebounced(): void {
  const now = Date.now();
  if (now - lastNudgeEmitAt < NUDGE_DEBOUNCE_MS) return;
  lastNudgeEmitAt = now;
  emitFollowNudge();
}

/** Phaser dialog chrome: follower Nudge btn + leader ack toast. */
export class FollowNudgePhaserChrome {
  private followerBtn: Phaser.GameObjects.Text | null = null;
  private leaderAck: Phaser.GameObjects.Text | null = null;
  private fadeTween: Phaser.Tweens.Tween | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number
  ) {}

  mountFollowerNudge(shell: DialogShellMetrics): void {
    const panelRight = shell.panelCx + shell.panelW * 0.5;
    const panelBottom = shell.panelBottom ?? shell.panelCy + shell.panelH * 0.5;
    const x = panelRight - shell.pad;
    const y = panelBottom - shell.pad;

    this.followerBtn = this.scene.add
      .text(x, y, "Nudge", {
        fontFamily: UI_FONT,
        fontSize: `${shell.buttonFont}px`,
        color: FOLLOW_NUDGE_GREEN,
        fontStyle: "600",
        backgroundColor: "#1a3324",
        padding: { x: 14, y: 8 },
      })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(this.depth)
      .setInteractive({ useHandCursor: true });

    this.followerBtn.on(
      "pointerdown",
      (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
        emitFollowNudgeDebounced();
      }
    );
  }

  mountLeaderAck(shell: DialogShellMetrics): void {
    const panelLeft = shell.panelCx - shell.panelW * 0.5;
    const panelBottom = shell.panelBottom ?? shell.panelCy + shell.panelH * 0.5;
    const x = panelLeft + shell.pad;
    const y = panelBottom - shell.pad - 8;

    this.leaderAck = this.scene.add
      .text(x, y, FOLLOW_NUDGED_ACK, {
        fontFamily: UI_FONT,
        fontSize: `${shell.buttonFont}px`,
        color: FOLLOW_NUDGE_GREEN,
        fontStyle: "600",
      })
      .setOrigin(0, 1)
      .setScrollFactor(0)
      .setDepth(this.depth + 1)
      .setAlpha(0)
      .setVisible(false);
  }

  showNudgedAck(): void {
    if (!this.leaderAck) return;
    this.fadeTween?.stop();
    this.leaderAck.setVisible(true).setAlpha(1);
    this.fadeTween = this.scene.tweens.add({
      targets: this.leaderAck,
      alpha: 0,
      duration: FOLLOW_NUDGE_FADE_MS,
      ease: "Linear",
      onComplete: () => {
        this.leaderAck?.setVisible(false);
        this.fadeTween = null;
      },
    });
  }

  destroy(): void {
    this.fadeTween?.stop();
    this.fadeTween = null;
    this.followerBtn?.destroy();
    this.leaderAck?.destroy();
    this.followerBtn = null;
    this.leaderAck = null;
  }
}

/** DOM narrative modal: follower Nudge + leader ack. */
export function syncNarrativeNudgeUi(opts: { readOnly: boolean }): void {
  if (typeof document === "undefined") return;
  const btn = document.getElementById("follow-nudge-btn");
  const ack = document.getElementById("follow-nudge-ack");
  if (!btn || !ack) return;

  const showFollower = opts.readOnly;
  btn.hidden = !showFollower;
  btn.style.display = showFollower ? "" : "none";
  ack.hidden = true;
  ack.textContent = FOLLOW_NUDGED_ACK;
  ack.classList.remove("flash");
}

export function hideNarrativeNudgeUi(): void {
  if (typeof document === "undefined") return;
  const btn = document.getElementById("follow-nudge-btn");
  const ack = document.getElementById("follow-nudge-ack");
  btn?.classList.remove("visible");
  if (btn) {
    btn.hidden = true;
    btn.style.display = "none";
  }
  if (ack) {
    ack.hidden = true;
    ack.classList.remove("flash");
  }
}

export function wireNarrativeNudgeButton(signal: AbortSignal): void {
  const btn = document.getElementById("follow-nudge-btn");
  if (!btn) return;
  btn.addEventListener(
    "click",
    (ev) => {
      ev.stopPropagation();
      emitFollowNudgeDebounced();
    },
    { signal }
  );
}

export function flashNarrativeNudgedAck(): void {
  const ack = document.getElementById("follow-nudge-ack");
  if (!ack) return;
  ack.textContent = FOLLOW_NUDGED_ACK;
  ack.hidden = false;
  ack.classList.remove("flash");
  void ack.offsetWidth;
  ack.classList.add("flash");
}
